import { useConfirmationModal } from '@openx/components-deal/lib/DealForm/hooks/useConfirmationModal.tsx';
import { useLockedPackageConfirmationModal } from '@openx/components-deal/lib/DealForm/hooks/useLockedPackageConfirmationModal.tsx';
import { usePackageConfirmationModal } from '@openx/components-deal/lib/DealForm/hooks/usePackageConfirmationModal.tsx';
import { useRboFormatValidationPopup } from '@openx/components-deal/lib/DealForm/hooks/useRboFormatValidationPopup.tsx';
import { initRboConfig, keysToExcludeFromPayload } from '@openx/components-deal/lib/DealForm/utils/constants.ts';
import {
  isChangingDealTypeFromWithinOpenAudience,
  isChangingDealTypeToAuction,
} from '@openx/components-deal/lib/DealForm/utils/dealFormUtils.ts';
import { isDynamicRboThatNeedsRecalc } from '@openx/components-deal/lib/DealForm/utils/isDynamicRboThatNeedsRecalc.ts';
import { fixDealStartAndEndDateFormat } from '@openx/components-deal/lib/DealForm/utils/parseDealDate.ts';
import { useFormatThirdPartyFeesConfig } from '@openx/components-deal/lib/DealForm/utils/useFormatThirdPartyFeesConfig.ts';
import { type DealProviderConfig, useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import { deepNormalizeValues, differenceByAllKeys } from '@openx/components-form/difference.ts';
import { getInitialPckValues } from '@openx/components-package/components/PackageForm/constants.ts';
import {
  getDiff,
  normalizeEstimates,
  normalizeGeographic,
} from '@openx/components-package/components/PackageForm/utils/formStateConverter.ts';
import { usePackageContext } from '@openx/components-package/utils/context/PackageContext.tsx';
import { usePackageEditPermissions } from '@openx/components-package/utils/hooks/usePackageEditPermissions.ts';
import { useStandaloneContextualSegmentsEnabled } from '@openx/components-targeting/Targeting/ContextualSegments/featureFlags.ts';
import { isTargetingEqualIgnoringSetOrder } from '@openx/components-targeting/Targeting/TargetingPresets/utils/normalizeRenderingContext.ts';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import type { DeepPartialNullable } from '@openx/types/api.ts';
import {
  type DealFormDataWithTargeting,
  type DealState,
  type DealStatus,
  dealFormField,
  pmpDealType,
  type ThirdPartyFeeConfig,
} from '@openx/types/deal.ts';
import type { PackageCreateParams, PackageUpdateParams } from '@openx/types/gqlTypes.ts';
import type { ConstValues } from '@openx/types/helpers.ts';
import { ParameterizedOptionsType } from '@openx/types/options.ts';
import { TargetingFields, type TargetingState } from '@openx/types/targeting/targeting.ts';
import { toUTCDateTime } from '@openx/utils/lib/date.ts';
import { OPEN_AUDIENCE_ORG_ID, OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config.ts';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { transformAudienceDetailsToInput } from '../../../modules/deals/utils/transformAudienceDetailsToInput.ts';
import { trackContextualSegmentsSaved } from '../../targeting/utils/trackContextualSegmentsSaved.ts';
import { useAudienceCreateAndActivate } from '../../targeting/utils/useAudienceCreateAndActivate.ts';

const pckgDTOkeys = ['targeting', 'url_targeting'];

const pckgUpdateDTOkeys: string[] = ['deal_id', 'deal_uid', ...pckgDTOkeys];

const pckgCreateDTOkeys: string[] = ['account_uid', 'name', ...pckgDTOkeys];

export const isDealFormDataWithTargeting = (data: DealFormDataWithTargeting): data is DealFormDataWithTargeting =>
  'targeting' in data && !!data.targeting;

const packageSaveStrategies = {
  createNewPackage: Symbol('create new package'),
  updateExistingPackage: Symbol('edit existing package'),
  doNotChange: Symbol('use existing package as it is'),
};

export const useDealFormSubmit: DealProviderConfig['useDealFormSubmit'] = ({
  isEdit,
  editedDealId,
  currentDeal,
  pckg,
  initialValues,
  pckgInitialValues,
  relatedDeals,
  pckgUid,
  pckgFetch,
}) => {
  const { isLocked: isPckgLocked } = usePackageEditPermissions(pckg || undefined);
  const { useDealFormActions } = useDealContext();
  const { showConfirmationModal } = useConfirmationModal();
  const { showPackageConfirmation } = usePackageConfirmationModal();
  const { showLockedPackageConfirmation } = useLockedPackageConfirmationModal();
  const { usePackageCreate, usePackageUpdate } = usePackageContext();
  const { submitDealForm, skipDealUpdate } = useDealFormActions();
  const { useCurrentInstanceState, useAudienceOptionsDetailsLazyFetch } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { onBehalfOrganization } = useDealContext();
  const { session } = useAuth();
  const { fetchAudienceDetails } = useAudienceOptionsDetailsLazyFetch();
  const { createAndActivateAudience } = useAudienceCreateAndActivate();
  const { safeFormatThirdPartyFeesConfig } = useFormatThirdPartyFeesConfig();

  const { showRboFormatConfirmation } = useRboFormatValidationPopup();
  const isStandaloneContextualSegments = useStandaloneContextualSegmentsEnabled();

  const [savingDeal, setSavingDeal] = useState<boolean>(false);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { createPackage, isCreating: _isCreating } = usePackageCreate();
  const { updatePackage, isUpdating: _isUpdating } = usePackageUpdate({ pckgUid: pckgUid || '' });

  const showRboConfirmation = useCallback(
    (formValues: DealFormDataWithTargeting) => {
      return new Promise((resolve, reject) => {
        const hasPackageUid = !!formValues.package_uid;
        const hasTargetingData = isDealFormDataWithTargeting(formValues);

        const group = formValues.__meta?.group_deal || [];
        const triggersRboRecalc = isDynamicRboThatNeedsRecalc(group, formValues.rbo_config);

        const dealEditAlertConfirmation =
          isEdit && triggersRboRecalc && (hasPackageUid || hasTargetingData || group.length !== 1);

        const statusHasChanged = initialValues?.status !== formValues.status;

        if (dealEditAlertConfirmation || (!isEdit && triggersRboRecalc)) {
          return showConfirmationModal({
            onConfirmed: () => {
              resolve('');
            },
            onCancel: () => {
              reject();
            },
            isEdit: isEdit,
            currentStatus: formValues.status as DealStatus,
            statusHasChanged,
          });
        }
        resolve('');
      });
    },
    [isEdit, showConfirmationModal, initialValues?.status]
  );

  const showPackageConfirmationConditionally = useCallback(
    (formValues: DealFormDataWithTargeting): Promise<ConstValues<typeof packageSaveStrategies>> => {
      return new Promise((resolve, reject) => {
        const havePackageUid = !!formValues.package_uid;
        const haveRelatedDeal =
          relatedDeals.count === 1 && relatedDeals.deals.some(deal => deal.name === currentDeal?.name);
        const isEditableAndHaveRelatedDeal = isEdit && haveRelatedDeal;

        if (!havePackageUid) {
          /** Unique Targeting, thus create new package */
          /** this also covers case of "curated package used as template", because in that case package_uid is null */
          resolve(packageSaveStrategies.createNewPackage);
          return;
        }

        if (havePackageUid && pckg?.is_curated) {
          /** Curated package is used as shared, thus use existing package as it is */
          resolve(packageSaveStrategies.doNotChange);
          return;
        }

        if (havePackageUid && !pckg?.is_curated) {
          /** Existing non-curated package is in use, but no changes has been made, thus use existing package as it is */
          if (
            isTargetingEqualIgnoringSetOrder(
              pckgInitialValues?.targeting,
              formValues[dealFormField.TARGETING]?.targeting
            )
          ) {
            resolve(packageSaveStrategies.doNotChange);
            return;
          }

          if (isPckgLocked) {
            /** Existing non-curated package is in use, changes has been made and package is locked, thus prompt user */
            showLockedPackageConfirmation({
              onCancel: () => {
                reject();
              },
              onUseAsTemplate: () => {
                resolve(packageSaveStrategies.createNewPackage);
              },
              onUseAsTargeting: () => {
                resolve(packageSaveStrategies.doNotChange);
              },
            });
            return;
          }

          if (relatedDeals.count === 0 || isEditableAndHaveRelatedDeal) {
            /** Existing non-curated package is in use, changes has been made but package is used by this deal only, thus update existing package */
            resolve(packageSaveStrategies.updateExistingPackage);
            return;
          }

          /** Existing non-curated package is in use, changes has been made and package is used by other deals, thus prompt user */
          showPackageConfirmation({
            onCancel: () => {
              reject();
            },
            onUseAsTemplate: () => {
              resolve(packageSaveStrategies.createNewPackage);
            },
            onConfirmChanges: () => {
              resolve(packageSaveStrategies.updateExistingPackage);
            },
            relatedDealsNames: relatedDeals.deals.map(deal => deal.name),
          });
          return;
        }

        /** Any other case should not happen, thus reject the Promise and prevent form submission */
        reject('Unexpected case upon package confirmation');
      });
    },
    [
      showPackageConfirmation,
      pckg?.is_curated,
      currentDeal?.name,
      isEdit,
      isPckgLocked,
      pckgInitialValues,
      relatedDeals.count,
      relatedDeals.deals,
      showLockedPackageConfirmation,
    ]
  );

  const checkCreateAudienceHandler = useCallback(
    async (packageObject: PackageCreateParams, isOpenXCuratedPackage: boolean, targetingAudienceUnchanged: boolean) => {
      const isSuborgAccount =
        session?.account?.name !== OPEN_AUDIENCE_ORG_NAME ||
        (onBehalfOrganization && onBehalfOrganization.id !== OPEN_AUDIENCE_ORG_ID);
      const userId = session?.uid;

      const audienceDetailsSummary = packageObject?.targeting?.audience?.[ParameterizedOptionsType.OPEN_AUDIENCES];
      const hasAudience = audienceDetailsSummary != null;
      const shouldCreateAudience =
        hasAudience &&
        isOpenXCuratedPackage &&
        targetingAudienceUnchanged &&
        isSuborgAccount &&
        userId &&
        audienceDetailsSummary;

      if (shouldCreateAudience) {
        const {
          data: { audience_by_pk: audienceDetails },
        } = await fetchAudienceDetails(audienceDetailsSummary.val as string);

        const audienceAccountId = onBehalfOrganization ? onBehalfOrganization.id : session.account_id;
        const audienceAcccountName = onBehalfOrganization ? onBehalfOrganization.name : session.account?.name;
        const updatedAudienceName = `${audienceAcccountName}: ${audienceDetails.name} [${new Date().toISOString()}]`;
        const preparedAudienceInputData = transformAudienceDetailsToInput(session.uid, audienceAccountId, {
          ...audienceDetails,
          name: updatedAudienceName,
        });

        const audienceId = await createAndActivateAudience(preparedAudienceInputData);

        enqueueSnackbar(
          t('Audience {audienceName} was successfully created', {
            audienceName: updatedAudienceName,
          }),
          { variant: 'success' }
        );

        if (audienceId && hasAudience) {
          audienceDetailsSummary.val = `openaudience-${audienceId}`;
        }
      }

      return packageObject;
    },
    [session, fetchAudienceDetails, createAndActivateAudience, enqueueSnackbar, t, onBehalfOrganization]
  );

  const doSavePackage = useCallback(
    async (
      formValues: DealFormDataWithTargeting,
      packageSaveStrategy: ConstValues<typeof packageSaveStrategies>
    ): Promise<string | null> => {
      if (!('targeting' in formValues)) {
        return null;
      }

      // normalize estimates and location radius to save values in the same format in each place
      formValues.targeting.estimates = normalizeEstimates(formValues.targeting.estimates);
      if (formValues.targeting?.targeting?.[TargetingFields.GEOGRAPHIC]) {
        formValues.targeting.targeting[TargetingFields.GEOGRAPHIC] = normalizeGeographic(
          formValues.targeting.targeting?.[TargetingFields.GEOGRAPHIC]
        );
      }

      if (packageSaveStrategy === packageSaveStrategies.createNewPackage) {
        const pckgFormData = cloneDeep(formValues.targeting);
        pckgFormData.name = `Package for {deal: ${formValues.name}} [${new Date().toISOString()}]`;
        pckgFormData.account_uid = formValues.account_uid || '';

        let pckgToCreate = pick(getDiff(pckgFormData, getInitialPckValues()), pckgCreateDTOkeys) as PackageCreateParams;

        /**
         * If package_uid is not present in form values, but targeting is, and pckg is loaded -
         * that means the pckg was used as template to prepopulate targeting on first render OR
         * the package is CURATED and deliberately used as template.
         *
         * IF user selects non-curated package or use curated package as shared targeting - package_uid would be present in form values.
         */
        if (!formValues.package_uid && pckg) {
          pckgToCreate.cloned_from = pckg.id;
        }

        if (pckg?.targeting.audience && pckgToCreate?.targeting?.audience) {
          const isOpenXCuratedPackage = !!pckg?.is_curated;
          const targetingAudienceUnchanged = isEqual(
            pckg.targeting.audience?.openaudience_custom?.val,
            pckgToCreate.targeting.audience?.openaudience_custom?.val
          );

          pckgToCreate = await checkCreateAudienceHandler(
            pckgToCreate,
            isOpenXCuratedPackage,
            targetingAudienceUnchanged
          );
        }

        const { uid } = await createPackage(pckgToCreate);
        return uid;
      }

      if (packageSaveStrategy === packageSaveStrategies.updateExistingPackage) {
        if (!pckgUid || !pckgInitialValues) {
          throw new Error('Trying to update package, but pckgUid is not defined');
        }
        const pckgUpdateData = pick(
          getDiff(formValues.targeting, pckgInitialValues),
          pckgUpdateDTOkeys
        ) as PackageUpdateParams;

        /** Temporary work-around fix, for bug on the Backend */
        await updatePackage({ ...pckgUpdateData, url_targeting: formValues.targeting.targeting.url_targeting });
        return null;
      }

      return null;
    },
    [pckgUid, pckg, updatePackage, createPackage, checkCreateAudienceHandler, pckgInitialValues]
  );

  const doSave = useCallback(
    async (
      formValues: DealFormDataWithTargeting,
      newPckgUid: string | null,
      savedTargeting: Partial<TargetingState> | null | undefined,
      isPackageUpdated: boolean
    ): Promise<void> => {
      /** if, upon submission, new package was created - newPckgUid would have a value (null otherwise) - which should be used instead one from formValues */

      const hasThirdPartyFeesConfig = formValues.third_party_fees_config_enabled;

      const shouldOpenAuctionAccessBeSend =
        formValues.pmp_deal_type !== pmpDealType.Guaranteed &&
        formValues.pmp_deal_type !== pmpDealType.WithinOpenAuction;

      const shouldExpectedAdCategoryBeSend =
        isBefInstance &&
        (formValues.pmp_deal_type === pmpDealType.WithinOpenAuction ||
          isChangingDealTypeFromWithinOpenAudience(currentDeal, formValues));

      const isPacingEnabled = formValues.is_pacing_enabled;

      const formValuesWithUtcStringDates = {
        ...fixDealStartAndEndDateFormat(formValues as DealFormDataWithTargeting),
        deal_id: formValues.deal_id?.trim(),
      };

      const initialValuesWithUtcStringDates = initialValues
        ? {
            ...fixDealStartAndEndDateFormat(initialValues),
            deal_id: initialValues.deal_id?.trim(),
          }
        : undefined;

      if (isChangingDealTypeToAuction(currentDeal, formValues)) {
        delete initialValuesWithUtcStringDates?.use_market_floor;
      }

      const deal = await Promise.resolve(
        isEdit
          ? differenceByAllKeys<DeepPartialNullable<DealFormDataWithTargeting>>(
              formValuesWithUtcStringDates,
              initialValuesWithUtcStringDates!
            )
          : deepNormalizeValues<DeepPartialNullable<DealFormDataWithTargeting>>(
              formValuesWithUtcStringDates,
              initialValuesWithUtcStringDates || {}
            )
      ).then(dealBaseDiff => {
        const dealDiff = omit(
          cloneDeep(dealBaseDiff) as DeepPartialNullable<DealFormDataWithTargeting>,
          keysToExcludeFromPayload
        );

        const { rbo_config } = dealDiff;
        if (rbo_config && !isEdit) {
          dealDiff.rbo_config = differenceByAllKeys(rbo_config, initRboConfig);
        }

        if (dealDiff.deal_price === '') {
          dealDiff.deal_price = null;
        }

        if (!shouldOpenAuctionAccessBeSend) {
          delete dealDiff.open_auction_access;
        }

        if (!shouldExpectedAdCategoryBeSend) {
          delete dealDiff.expected_ad_category;
        }

        delete dealDiff.third_party_fees_config_enabled;

        if (dealDiff.third_party_fees_config) {
          dealDiff.third_party_fees_config = safeFormatThirdPartyFeesConfig(
            formValues.third_party_fees_config_enabled,
            // the diff of two Array<T> always returns `null` or new version of the `Array<T>`, not `DeepPartialNullable<Array<T>>`
            dealDiff.third_party_fees_config as ThirdPartyFeeConfig[]
          );
        }

        if (!hasThirdPartyFeesConfig) {
          delete dealDiff.curated_deal_buyer_discount_enabled;
        }

        const isUsedAsTemplate = !formValues?.package_uid && !!currentDeal;
        if (isDealFormDataWithTargeting(formValues) && isUsedAsTemplate) {
          delete dealDiff?.use_market_floor;
        }

        /** Targeting is saved along with package, therefore remove it from deal data */
        delete dealDiff.targeting;

        if (newPckgUid) {
          dealDiff.package_uid = newPckgUid;
        }

        // trigger only when user enables pacing
        if (isEdit && dealDiff.is_pacing_enabled) {
          dealDiff.start_date = dealDiff.start_date || toUTCDateTime((currentDeal as DealState).start_date);
          dealDiff.end_date = dealDiff.end_date || toUTCDateTime((currentDeal as DealState).end_date);
          dealDiff.account_uid = (currentDeal as DealState).account_uid;
          dealDiff.instance_uid = (currentDeal as DealState).instance_uid;
        }

        // trigger only when user disabled pacing
        if (isEdit && dealDiff.is_pacing_enabled === false && (currentDeal as DealState).monetizing_uid) {
          dealDiff.monetizing_uid = null;
        }

        if (!isEdit) {
          /** if new deal is about to be created, create new monetizing as well (covers useAsTemplate/Targeting) */
          delete dealDiff.monetizing_uid;
        }

        // needed all values when cadence is updated
        if (isEdit && (dealDiff.budget_mode || dealDiff.cadence_type || dealDiff.monetizing_budget)) {
          dealDiff.budget_mode = dealDiff.budget_mode || (currentDeal as DealState).budget_mode;
          dealDiff.cadence_type = dealDiff.cadence_type || (currentDeal as DealState).cadence_type;
          dealDiff.monetizing_budget = dealDiff.monetizing_budget || (currentDeal as DealState).monetizing_budget;
        }

        // If media_spend_goal was cleared, send pdp_config as null instead of { media_spend_goal: null }
        if (dealDiff.pdp_config?.media_spend_goal === null) {
          dealDiff.pdp_config = null;
        }

        // amazon_config (budget) is a Publisher-only field; never send it from OA UI.
        delete dealDiff.amazon_config;

        if (typeof dealBaseDiff.fixed_cpm_enabled === 'boolean') {
          dealDiff.fixed_cpm_enabled = dealBaseDiff.fixed_cpm_enabled;
        }

        return dealDiff;
      });

      const onSuccess = () =>
        trackContextualSegmentsSaved({
          entity: 'deal',
          isEdit,
          isStandaloneContextualSegments,
          targeting: savedTargeting,
          initialTargeting: initialValues?.targeting?.targeting,
        });

      /** Deal was not changed - no need to submit. Track only if its targeting was saved through the existing package */
      if (isEmpty(deal) && editedDealId) {
        if (isPackageUpdated) {
          onSuccess();
        }
        return skipDealUpdate(editedDealId);
      }

      await submitDealForm({
        deal,
        dealId: editedDealId,
        fallbackValues: formValues,
        isPacingEnabled,
        navigate,
        onSuccess,
      });
    },
    [
      currentDeal,
      isStandaloneContextualSegments,
      isBefInstance,
      isEdit,
      submitDealForm,
      editedDealId,
      initialValues,
      navigate,
      safeFormatThirdPartyFeesConfig,
      skipDealUpdate,
    ]
  );

  const submit = useCallback(
    async (formValues: DealFormDataWithTargeting) => {
      let newPckgUid: null | string = null;
      try {
        const packageSaveStrategy = await showPackageConfirmationConditionally(formValues);
        await showRboConfirmation(formValues);
        const nextFormValues = await showRboFormatConfirmation(formValues);
        setSavingDeal(true);

        try {
          newPckgUid = await doSavePackage(nextFormValues, packageSaveStrategy);
          /** With `doNotChange` the deal keeps the existing package, so form targeting edits are discarded (e.g. locked package used as-is) */
          const savedTargeting =
            packageSaveStrategy === packageSaveStrategies.doNotChange
              ? pckgInitialValues?.targeting
              : nextFormValues.targeting?.targeting;
          await doSave(
            nextFormValues,
            newPckgUid,
            savedTargeting,
            packageSaveStrategy === packageSaveStrategies.updateExistingPackage
          );
        } catch (e) {
          /** If package was created, but deal saving failed - form should be aware of newly created package uid */
          if (newPckgUid !== null && newPckgUid !== pckgUid) {
            pckgFetch?.(newPckgUid);
          }
          // biome-ignore lint/suspicious/noConsole: logging error in console
          console.error(e);
          /* Error upon saving package or deal */
        }
      } catch (_e) {
        /* Modal was cancelled */
      } finally {
        setSavingDeal(false);
      }
    },
    [
      pckgFetch,
      pckgInitialValues,
      doSave,
      doSavePackage,
      pckgUid,
      showRboConfirmation,
      showPackageConfirmationConditionally,
      showRboFormatConfirmation,
    ]
  );

  return {
    submit,
    savingDeal,
  };
};
